import { useCallback, useEffect, useState } from "react";
import { FiClock, FiTrash2, FiX } from "react-icons/fi";
import csrOutreachService from "../../api/services/csrOutreachService";
import { formatCallDuration, formatCallDurationClock } from "../../utils/callDuration";
import { ListSkeleton } from "../../components/common/skeletons";

const THEME = { primary: "#FB565F" };

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
}

export default function CsrCallHistoryModal({
  customer,
  onClose,
  onUpdated,
  canDelete = false,
  onDelete,
  deletingId = null,
  dataVersion = 0,
}) {
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [totals, setTotals] = useState({ totalCalls: 0, totalDurationSec: 0, totalMinutes: 0 });

  const load = useCallback(async () => {
    if (!customer?.userId) return;
    setLoading(true);
    try {
      const data = await csrOutreachService.getCallHistory(customer.userId);
      setCalls(data.calls || []);
      setTotals({
        totalCalls: data.totalCalls ?? (data.calls || []).length,
        totalDurationSec: data.totalDurationSec ?? 0,
        totalMinutes: data.totalMinutes ?? 0,
      });
    } catch {
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, [customer?.userId]);

  useEffect(() => {
    load();
  }, [load, dataVersion]);

  const handleDelete = (call) => {
    onDelete?.(call);
  };

  const name = [customer?.firstName, customer?.lastName].filter(Boolean).join(" ").trim();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <FiClock style={{ color: THEME.primary }} /> Call history
            </h3>
            <p className="text-sm text-gray-600 mt-0.5">{name || "Customer"}</p>
            <p className="text-xs text-gray-400">{customer?.email || "—"}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 text-sm flex flex-wrap gap-4">
          <span>
            <span className="text-gray-500">Records:</span>{" "}
            <strong className="text-gray-900">{totals.totalCalls}</strong>
          </span>
          <span>
            <span className="text-gray-500">Total time:</span>{" "}
            <strong className="text-gray-900">{formatCallDuration(totals.totalDurationSec)}</strong>
            <span className="text-gray-400 text-xs ml-1">
              ({Number(totals.totalMinutes || 0).toFixed(1)} min)
            </span>
          </span>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {loading ? (
            <ListSkeleton rows={6} />
          ) : !calls.length ? (
            <p className="text-center text-gray-400 py-12">
              No call records yet. Use <strong>Log time</strong> or call from chat.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">#</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Date & time</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Duration</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Source</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">CSR</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Notes</th>
                    {canDelete ? (
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 w-16">
                        Delete
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call, index) => (
                    <tr key={call.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-400 text-xs">{calls.length - index}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-800">
                        {formatWhen(call.startedAt)}
                      </td>
                      <td className="px-3 py-2 font-semibold text-gray-900 whitespace-nowrap">
                        {formatCallDurationClock(call.durationSec)}
                        <span className="block text-[10px] font-normal text-gray-400">
                          {formatCallDuration(call.durationSec)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                            call.source === "auto"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {call.sourceLabel}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        <p className="font-medium text-gray-800">{call.agentName || "—"}</p>
                        {call.agentEmail ? (
                          <p className="text-gray-400 truncate max-w-[140px]">{call.agentEmail}</p>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 max-w-[180px]">
                        {call.notes || "—"}
                      </td>
                      {canDelete ? (
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleDelete(call)}
                            disabled={deletingId === call.id}
                            className="inline-flex items-center justify-center p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                            title="Delete record (Developer only)"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 px-5 py-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              load();
              onUpdated?.();
            }}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-white"
            style={{ backgroundColor: THEME.primary }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
